import { getProviderBaseAuditInfo } from './utils';
import { sortPracticesByWorkedDays, practiceHasData } from './dataTransformations';

/**
 * Per-practice Base/Over Base status, computed with the SAME method the report uses to
 * decide the provider is on base (getProviderBaseAuditInfo/isProviderOverBase — pay minus
 * financial notes vs base pay for "Base Threshold" contracts, bonus eligibility for "Sum
 * Of"), just applied to one practice's numbers instead of the provider's totals. This is
 * deliberately NOT the backend's row.data[practice].isPracticeOverBase field — that field
 * compares practiceTotalPay (financial notes still included) against practicePayBasedOnDailyRate,
 * a different formula that can disagree with the provider-level result for reasons that have
 * nothing to do with the provider actually working more than one practice. Using the same
 * formula at both levels means the only way a practice's status can differ from the
 * provider's Final Status is the real, meaningful reason: this practice's own numbers pulled
 * one way while another practice (or contract type) pulled the other.
 */
const computePracticeStatus = (group, practiceObj) => {
    if (!practiceObj) return null;
    if (group === 'Sum Of') {
        return (parseFloat(practiceObj.practiceBonus) || 0) > 0;
    }
    if (group === 'Base Threshold') {
        const practicePay = parseFloat(practiceObj.practiceTotalPay) || 0;
        const practiceFinNotes = parseFloat(practiceObj.financialNotes) || 0;
        const practiceBasePay = parseFloat(practiceObj.practicePayBasedOnDailyRate) || 0;
        return Math.round((practicePay - practiceFinNotes) * 100) > Math.round(practiceBasePay * 100);
    }
    return null;
};

/**
 * Builds the audit rows behind the "Base Pay X%" badge modal — one row per provider that
 * counts toward the badge's numerator/denominator, using the exact same gate as
 * updateTotals()'s dentistAbbrArr/baseDentists (dataTransformations.js) and the same
 * getProviderBaseAuditInfo() classification the badge itself is built from, so this list
 * can never disagree with the number it explains.
 *
 * Each row also carries a `practices` list (same sortPracticesByWorkedDays + practiceHasData
 * filtering the main report's expandable practice rows use), each entry tagged with
 * computePracticeStatus() above — not the backend's isPracticeOverBase field.
 *
 * Modal-only display nuance (does NOT touch isProviderOverBase()/updateTotals()/the live
 * Base Pay badge): mirrors the existing "Other Practice Making Base" filter's own condition
 * exactly — more than one worked practice AND a genuine mix (at least one over base, at
 * least one not). Only then is the provider shown here as Over Base even when the
 * provider-level aggregate says Base; a single-practice provider is never affected by this,
 * no matter what that one practice's status is. `rawFinalStatus` keeps the untouched,
 * counter-matching value; `finalStatus` is what the row displays; `finalStatusForced` flags
 * when the two differ.
 *
 * This is a plain, on-demand function (not wired into the table's render loop) — call it
 * only when the modal is actually being opened.
 */
export const buildBaseAuditRows = (rows, getDoctorFullName, getPracticeName) => {
    const auditRows = [];
    (rows || []).forEach(row => {
        if (!row.ABBR || row.contractType === 'UKG HYN' || row.contractType === 'TMPDOC' || row.contractType === 'TMPHYN') {
            return;
        }

        const info = getProviderBaseAuditInfo(row);

        const practiceKeys = sortPracticesByWorkedDays(Object.keys(row.data || {}), row.data)
            .filter(practice => practiceHasData(row.data[practice]));

        const practices = practiceKeys.map(practiceKey => ({
            name: getPracticeName?.(practiceKey) || practiceKey,
            isPracticeOverBase: computePracticeStatus(info.group, row.data[practiceKey]),
        }));

        // Same condition as the "Other Practice Making Base" filter in CdpReportsTableByDentists.js:
        // filters.otherPracticeMakingBase (more than one worked practice, and both an
        // over-base and a not-over-base practice among them).
        const hasMultiplePractices = practices.length > 1;
        const hasOverBasePractice = practices.some(p => p.isPracticeOverBase === true);
        const hasNotOverBasePractice = practices.some(p => p.isPracticeOverBase === false);
        const finalStatusForced = info.group === 'Base Threshold' && info.finalStatus === 'Base' &&
            hasMultiplePractices && hasOverBasePractice && hasNotOverBasePractice;

        auditRows.push({
            abbr: row.ABBR,
            doctorName: row.doctorName || getDoctorFullName?.(row.ABBR) || row.ABBR,
            contractType: row.contractType,
            practices,
            ...info,
            rawFinalStatus: info.finalStatus,
            finalStatus: finalStatusForced ? 'Over Base' : info.finalStatus,
            finalStatusForced,
        });
    });
    return auditRows;
};
